/**
 * Employee invite-accept action — EMP-05 / CC-52 (be-050 wiring spec).
 *
 * Two-step flow per be-050 §5b/§5c:
 *
 *   1) POST /api/v1/auth/invite/accept with `X-Tenant-Slug` header +
 *      `{token, password}` JSON body. Backend validates the invite token,
 *      enforces PasswordPolicy (length ≥ 8 only since CC-155 — no complexity
 *      rules), consumes the invite, and returns a TokenResponse. We DISCARD
 *      those tokens (step 2 mints the real session) but we KEEP one field:
 *      `audience` (be-788 / CC-431). It used to be `aud=admin` unconditionally
 *      — a real hole, since a plain employee got admin-audience tokens — and
 *      is now `"mobile"` for plain employees. It is the backend's verdict on
 *      WHICH APP owns this session, so it, not the app the invite link
 *      happened to open in, decides the audience of the chained login.
 *      We also defensively clear the access + refresh stores between step 1
 *      and step 2 so a failure-after-accept never leaves a half-authenticated
 *      zombie session in memory.
 *
 *   2) POST /api/v1/auth/login with `{email, password, tenantSlug,
 *      audience}` via the existing `performLogin` action — it handles token
 *      storage (under the RESOLVED audience's key), JWT decode,
 *      scheduleProactiveRefresh, and returns the same `LoginResult`
 *      discriminated union as a normal login. The `email` is the
 *      backend-canonical address fetched by the form via the invite preview
 *      endpoint (CC-56) — never re-typed by the user.
 *
 * Error contract:
 *   - Accept failure → throws an ApiError-shaped object. The form maps
 *     `errorCode` to localized inline messages (AUTH_INVITE_INVALID,
 *     AUTH_WEAK_PASSWORD, AUTH_RATE_LIMITED).
 *   - Login failure AFTER accept-success → the thrown error carries
 *     `phase: "login"` plus the resolved `audience`. The invite is already
 *     consumed, so the form must NOT offer "request a new invite"; instead it
 *     tells the user their password is set and routes them to the login screen
 *     of the app that owns the session.
 */
import { performLogin } from "./login.js";
import { getAcceptLanguageHeader } from "../refresh/authNetworkConfig.js";
import type { LoginResult } from "./login.js";
import type { Audience } from "../types.js";
import { accessTokenStore } from "../tokens/accessTokenStore.js";
import { refreshTokenStore } from "../tokens/refreshTokenStore.js";

export interface InviteAcceptArgs {
  /** Opaque invite token from the email link (`?token=...`). */
  token: string;
  /** New password chosen by the invitee. Backend enforces PasswordPolicy. */
  password: string;
  /**
   * User-entered email (no preview endpoint shipped — see file header).
   * Required for the second-step /auth/login call.
   */
  email: string;
  /** Tenant slug — query string wins over subdomain per be-050 §3. */
  tenantSlug: string;
  apiBaseUrl: string;
  /**
   * Audience of the app the invite link was opened in — the FALLBACK for the
   * chained `/auth/login` call. `useAuth().acceptInvite` injects it from
   * `useAuthAudience()` so route code never has to thread it explicitly.
   *
   * Used only when the accept response omits `audience` (older backends, or a
   * value we don't recognise). When the backend does report one it wins: an
   * employee whose invite mail pointed at the admin surface must still get an
   * `aud=mobile` session, not an admin login that 403s with
   * AUTH_ADMIN_AUDIENCE_NOT_PERMITTED.
   */
  audience: Audience;
}

/** Narrow the accept response's free-form `audience` string to our union. */
function toAudience(value: unknown): Audience | null {
  return value === "admin" || value === "mobile" || value === "platform" || value === "accounting"
    ? value
    : null;
}

export async function performInviteAccept(args: InviteAcceptArgs): Promise<LoginResult> {
  // Step 1 — accept the invite. Backend consumes the token + sets the
  // password atomically. Its tokens are discarded; only `audience` is kept.
  const acceptRes = await fetch(args.apiBaseUrl + "/api/v1/auth/invite/accept", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...getAcceptLanguageHeader(),
      "X-Tenant-Slug": args.tenantSlug,
    },
    body: JSON.stringify({ token: args.token, password: args.password }),
  });

  if (!acceptRes.ok) {
    const body = (await acceptRes.json().catch(() => ({}))) as {
      errorCode?: string;
      title?: string;
    };
    throw Object.assign(new Error(body.title ?? "Invite accept failed"), {
      status: acceptRes.status,
      errorCode: body.errorCode ?? "AUTH_INVITE_INVALID",
      correlationId: acceptRes.headers.get("x-correlation-id"),
      isApiError: true,
    });
  }

  // The ONE field we keep from the accept response: which app owns the session
  // the invitee just activated. Everything else (the tokens) is discarded.
  const acceptBody = (await acceptRes.json().catch(() => ({}))) as { audience?: string | null };
  const sessionAudience = toAudience(acceptBody.audience) ?? args.audience;

  // Defensive: clear any stale stores so a failure in step 2 never leaves
  // an authenticated zombie session. We intentionally do NOT call
  // accessTokenStore.set with the response body — the accept tokens are
  // short-lived and step 2 mints the session we actually keep.
  accessTokenStore.clear();
  refreshTokenStore.clear();

  // Step 2 — re-login under the calling app's audience. performLogin
  // handles all token storage + scheduleProactiveRefresh + JWT claim
  // decoding for us, and returns the same {kind:"success"|"otp-required"}
  // discriminated union the caller would get from a normal /login.
  //
  // The invite is ALREADY consumed at this point, so a failure here is not
  // recoverable by re-accepting. Tag it `phase: "login"` so the form steers
  // the user to a login screen ("your password is set, sign in") instead of the
  // misleading "request a new invite" terminal state — and carry the resolved
  // audience so the form knows WHICH app's login screen that is.
  try {
    return await performLogin({
      identifier: args.email,
      password: args.password,
      tenantSlug: args.tenantSlug,
      audience: sessionAudience,
      apiBaseUrl: args.apiBaseUrl,
    });
  } catch (err) {
    if (err !== null && typeof err === "object") {
      Object.assign(err, { phase: "login", audience: sessionAudience });
    }
    throw err;
  }
}
